import { useEffect, useRef, useState } from 'react';

// AGGIUNGERE AVVIENE IN LINEA. «Add» promette che una cosa esista, non che si apra un modulo da
// compilare prima che esista: la riga nasce subito, con un nome libero, e diventa il soggetto.
//
// IL PROBLEMA È CHE LA COSA NASCE ALTROVE. Chi crea una luce o una camera lo chiede al motore 3D,
// e la riga compare solo dopo che la scena è stata riletta — un giro che passa per uno stato di
// React e a volte per un `setTimeout`. Al momento del clic l'id della riga nuova non esiste
// ancora, quindi non c'è niente da selezionare: si dichiara il NOME che sta per comparire, e
// appena compare la riga si sceglie da sé.
//
// Il nome basta perché è libero per costruzione: chi crea lo prende da `freeName`, che non
// restituisce mai un nome già in uso.

export interface BornSubject {
    id: string;
    name: string;
    children?: BornSubject[];
}

const findByName = <N extends BornSubject>(nodes: N[], name: string): N | undefined => {
    for (const node of nodes) {
        if (node.name === name) return node;
        const inside = node.children?.length ? findByName(node.children as N[], name) : undefined;
        if (inside) return inside as N;
    }
    return undefined;
};

/** Tutti i nomi già presi, gruppi compresi: due righe con lo stesso nome sarebbero indistinguibili. */
export const takenNames = (nodes: BornSubject[]): string[] => nodes.flatMap((node) => [node.name, ...(node.children?.length ? takenNames(node.children) : [])]);

/** «New light», poi «New light 2», «New light 3». Il primo non porta il numero: da solo non conta. */
export const freeName = (nodes: BornSubject[], base: string): string => {
    const taken = new Set(takenNames(nodes));
    if (!taken.has(base)) return base;
    let n = 2;
    while (taken.has(`${base} ${n}`)) n++;
    return `${base} ${n}`;
};

/**
 * Restituisce la funzione con cui si dichiara il nome atteso. Chiamata dopo aver creato, aspetta
 * che la riga compaia nell'elenco e allora la sceglie.
 */
export const useBornSubject = <N extends BornSubject>(nodes: N[], select: (node: N) => void) => {
    const [expectedName, setExpectedName] = useState<string | null>(null);

    // `select` è una funzione nuova a ogni render: fra le dipendenze dell'effetto lo farebbe
    // ripartire in continuazione, e l'effetto sceglie — cioè cambia stato.
    const selectRef = useRef(select);
    selectRef.current = select;

    useEffect(() => {
        if (!expectedName) return;

        const born = findByName(nodes, expectedName);
        if (!born) return;

        setExpectedName(null);
        selectRef.current(born);
    }, [expectedName, nodes]);

    return setExpectedName;
};
